import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent } from '../../shared/components/table/table.component';
import { TableColumn } from '../../core/models/column.model';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { GeneralComponent } from '../general/general.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateRecord } from '../../core/models/update-record.model';
import { ModalComponent } from '../modal/modal.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ShiftClosureService } from '../../core/services/shift-closure.service';
import { ShiftClosure } from '../../core/models/shift-closure.model';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FormControl } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-historical-shifts',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    RouterModule,
    MatTableModule,
    MatTabsModule,
    GeneralComponent,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule
  ],
  templateUrl: './historical-shifts.component.html',
  styleUrl: './historical-shifts.component.scss',
})
export class HistoricalShiftsComponent implements OnInit {
  shiftClosures: any[] = [];
  filteredShiftClosures: any[] = [];
  availableTypes: string[] = [];
  
  // Form controls para los filtros
  typeFilter = new FormControl('all');
  dateFilter = new FormControl('');
  
  // Fecha máxima permitida (hoy)
  maxDate = new Date();

  tableColumns: TableColumn[] = [
  //  { header: 'Responsable', field: 'responsable'},
    { header: 'Tipo', field: 'tipo' },
    { header: 'Fecha', field: 'fecha', isDate: true },
    { header: 'Hora Inicio', field: 'hora_inicio', isTime: true},
    { header: 'Hora Fin', field: 'hora_fin', isTime: true },
  ];

  constructor(
    private shiftClosureService: ShiftClosureService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.loadData();
    
    // Configurar listeners para los filtros
    this.typeFilter.valueChanges.subscribe(() => {
      this.applyFilters();
    });
    
    this.dateFilter.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  loadData(): void {
    console.log('Cargando datos de turnos históricos...');
    // Decidir endpoint según el rol: DCS/Férrico usan /user/{id}; Novedades (otros) usan /shiftclosure/
    const isDcs = this.auth.hasRole('Novedades_Operador_DCS');
    const isFerrico = this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto');
    const isJefeTurno = this.auth.hasRole('novedades');

    const source$ = (isDcs || isFerrico)
      ? this.shiftClosureService.getByUser()
      : this.shiftClosureService.getAllShiftClosures();

    source$
      .subscribe({
        next: (records: ShiftClosure[]) => {
          console.log('Registros básicos obtenidos:', records.length);
          
          if (records.length === 0) {
            this.shiftClosures = [];
            return;
          }

          // Si es jefe de turno, filtrar para mostrar solo:
          // - Sus propios informe_turno
          // - Todos los turno_diario
          let filteredRecords = records;
          if (isJefeTurno) {
            const currentUserId = this.auth.getUserId();
            filteredRecords = records.filter(record => {
              const isTurnoDiario = record.shiftClosureType?.code === 'turno_diario';
              const isOwnInformeTurno = record.shiftClosureType?.code === 'informe_turno' && record.user?.id === currentUserId;
              return isTurnoDiario || isOwnInformeTurno;
            });
            console.log(`Jefe de turno: Filtrados ${filteredRecords.length} de ${records.length} registros`);
          }

          // Ahora obtenemos los detalles completos de cada turno usando getShiftClosureById
          // Esto nos asegura obtener el valor correcto de isReportAvailable
          const detailRequests = filteredRecords.map(record => 
            this.shiftClosureService.getShiftClosureById(record.id).pipe(
              catchError(error => {
                console.error(`Error al obtener detalles del turno ${record.id}:`, error);
                // Si falla, devolvemos el registro original con isReportAvailable = false
                return of({ ...record, isReportAvailable: false });
              })
            )
          );

          // Ejecutamos todas las peticiones en paralelo
          forkJoin(detailRequests).subscribe({
            next: (detailedRecords: ShiftClosure[]) => {
              console.log('Detalles completos obtenidos:', detailedRecords);
              
              // Verificamos específicamente los registros 42 y 43
              const record42 = detailedRecords.find(r => r.id === 42);
              const record43 = detailedRecords.find(r => r.id === 43);
              
              if (record42) {
                console.log('Record 42 con detalles completos:', record42);
                console.log('Record 42 isReportAvailable:', record42.isReportAvailable);
              }
              if (record43) {
                console.log('Record 43 con detalles completos:', record43);
                console.log('Record 43 isReportAvailable:', record43.isReportAvailable);
              }

              const flattened = this.flattenData(detailedRecords);
              console.log('Datos aplanados finales:', flattened);
              
              this.shiftClosures = flattened;
              this.setupFilters();
              this.cdr.detectChanges();
            },
            error: error => {
              console.error('Error al obtener detalles de los turnos:', error);
              // Fallback: usar datos básicos sin isReportAvailable
              const flattened = this.flattenData(filteredRecords);
              this.shiftClosures = flattened;
              this.setupFilters();
            }
          });
        },
        error: error => {
          if (error.status === 404) {
            this.shiftClosures = [];
            this.filteredShiftClosures = [];
            this.setupFilters();
          } else {
            console.error(`Error al cargar los cierres de turno:`, error);
            this.shiftClosures = [];
            this.filteredShiftClosures = [];
            this.setupFilters();
          }
        },
      });
  }

  flattenData(records: ShiftClosure[]): any[] {
    return records.map(record => {
      const result = {
        id: record.id,
        responsable: record.user?.name || 'Sin usuario', // Nombre del usuario (jefe de turno)
        fecha: record.createdUtc || 'Sin fecha', // Fecha del cierre de turno
        hora_inicio: record.startUtc || 'Sin fecha',
        hora_fin: record.endUtc || 'Sin fecha',
        tipo: record.shiftClosureType.name,
        codigo_tipo: record.shiftClosureType.code,
        isReportAvailable: Boolean(record.isReportAvailable), // Usar el mismo valor que hasShiftReport
      };

      // Log para debugging de registros específicos
      if (record.id === 42 || record.id === 43) {
        console.log(`Mapeando record ${record.id}:`, {
          original: record.isReportAvailable,
          mapped: result.isReportAvailable
        });
      }

      return result;
    });
  }
  

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  viewUpdateRecord(updateRecord: UpdateRecord): void {
    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        record: updateRecord, // Pasar el objeto directamente
        readOnly: true, // Abrir en modo solo lectura
      },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  exportPdf(shiftClosureId: number): void {
    console.log(`Exportando PDF para shiftClosureId: ${shiftClosureId}`);

    // Generar la fecha con formato "YYYY-MM-DD"
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    const dateString = `${day}-${month}-${year}`;

    this.shiftClosureService.getShiftClosurePdf(shiftClosureId).subscribe((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shift_closure_${shiftClosureId}_${dateString}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      console.log(`PDF descargado exitosamente para shiftClosureId: ${shiftClosureId}`);
    });
  }

  navigateToDetail(shiftClosureId: number, tipo: string, codigoTipo: string): void {
    console.log('Navegando a historical-shift-detail con parámetros:', {
      shiftClosureId,
      tipo,
      codigoTipo
    });
    
    this.router.navigate(['/historical-shift-detail', shiftClosureId, tipo, codigoTipo])
      .then(success => {
        console.log('Navegación exitosa:', success);
      })
      .catch(error => {
        console.error('Error en la navegación:', error);
      });
  }

  setupFilters(): void {
    // Extraer tipos únicos de los datos
    const typesSet = new Set<string>();
    this.shiftClosures.forEach(shift => {
      if (shift.tipo) {
        typesSet.add(shift.tipo);
      }
    });
    
    this.availableTypes = Array.from(typesSet).sort();
    console.log('Tipos disponibles para filtro:', this.availableTypes);
    
    // Aplicar filtros iniciales
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.shiftClosures];
    
    // Filtro por tipo
    const selectedType = this.typeFilter.value;
    if (selectedType && selectedType !== 'all') {
      filtered = filtered.filter(shift => shift.tipo === selectedType);
    }
    
    // Filtro por fecha
    const selectedDate = this.dateFilter.value;
    if (selectedDate) {
      const filterDate = new Date(selectedDate);
      // Normalizar fecha seleccionada a medianoche para comparación
      filterDate.setHours(0, 0, 0, 0);
      
      filtered = filtered.filter(shift => {
        if (!shift.fecha || shift.fecha === 'Sin fecha') return false;
        
        const shiftDate = new Date(shift.fecha);
        shiftDate.setHours(0, 0, 0, 0);
        
        return shiftDate.getTime() === filterDate.getTime();
      });
    }
    
    this.filteredShiftClosures = filtered;
    console.log('Datos filtrados:', {
      total: this.shiftClosures.length,
      filtered: this.filteredShiftClosures.length,
      typeFilter: selectedType,
      dateFilter: selectedDate
    });
  }

  clearFilters(): void {
    this.typeFilter.setValue('all');
    this.dateFilter.setValue('');
  }
  
}
